import { css } from '@emotion/react';
import { faScrewdriverWrench } from '@fortawesome/pro-solid-svg-icons';
import {
  useCallback, useMemo, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import type { MapTool } from '~/_shared/types/toolsAndFeatures/mapTools.types';
import {
  getIconForMapToolOrFeature, getTranslationForMapToolOrFeature,
} from '~/_shared/types/toolsAndFeatures/mapToolsAndFeatures.helpers';
import { useTranslation } from '~/_shared/utils/hooks';
import { SettingsAccordionDropdownComponent } from '~/map/settings/accordion/settingsAccordionDropdown.component';
import { SharedMapToolDropDownOptionComponent } from '~/map/settings/accordion/sharedPresentationalMaps/sharedMapToolDropDownOption.component';
import { publicMapSettingsMenuToolsSetDefaultTool } from '~/store/mapSettings/publicMapSettings/mapSettingsPublicMapSettings.actionCreators';
import {
  usePublicMapSettingDefaultPresentationalMapToolSelector, usePublicMapSettingEnabledPresentationalMapToolsSelector,
} from '~/store/mapSettings/publicMapSettings/mapSettingsPublicMapSettings.selectors';

const iconStyle = css({
  fontSize: 18,
});

export const useSharedPresentationalMapsToolsMenuDropdown = () => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [t] = useTranslation();
  const dispatch = useDispatch();

  const enabledPresentationalMapTools = usePublicMapSettingEnabledPresentationalMapToolsSelector();
  const defaultPresentationalMenuTool = usePublicMapSettingDefaultPresentationalMapToolSelector();

  const onSharedMapToolDropDownChange = useCallback((tool: MapTool) => () => (
    dispatch(publicMapSettingsMenuToolsSetDefaultTool(tool))
  ), [dispatch]);

  const onDefaultToolAccordionHeaderClick = useCallback(() => {
    setIsDropdownOpen(isOpen => !isOpen);
  }, []);

  return useMemo(() => {
    const renderedOptions = enabledPresentationalMapTools
      .map((tool, index) => (
        <SharedMapToolDropDownOptionComponent
          isLast={enabledPresentationalMapTools.length === index + 1}
          key={tool}
          onChange={onSharedMapToolDropDownChange(tool)}
          mapTool={tool}
        />
      ));

    const triggerComponent = (
      <SettingsAccordionDropdownComponent
        isOpen={isDropdownOpen}
        onClick={onDefaultToolAccordionHeaderClick}
        triggerText={defaultPresentationalMenuTool ? getTranslationForMapToolOrFeature(defaultPresentationalMenuTool) : t('No Tools')}
      >
        <FontAwesomeIcon
          css={iconStyle}
          icon={defaultPresentationalMenuTool ? getIconForMapToolOrFeature(defaultPresentationalMenuTool) : faScrewdriverWrench}
        />
      </SettingsAccordionDropdownComponent>
    );

    return {
      isOpen: isDropdownOpen,
      onClose: () => setIsDropdownOpen(false),
      renderedOptions,
      triggerComponent,
      value: defaultPresentationalMenuTool,
    };
  }, [enabledPresentationalMapTools, isDropdownOpen, onDefaultToolAccordionHeaderClick, t,
    defaultPresentationalMenuTool, onSharedMapToolDropDownChange]);
};
